// Column edit planner for the ALTER TABLE source path. Each op appends a
// fresh ALTER TABLE statement after the source statement (DSE7: no
// coalescing, every applyDdlEdit call is independent). The synthesized SQL
// is dialect-native via `dialect.ts` synthesizers; the byte-edit shape stays
// parity-required (always a single PlannedByteEdit per DSE6).
//
// PRD: Ralph/DdlStructuralEdit/PRD.md § Slice: Column Edit Applicator.

import { rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { IdentifierQuoting } from '../../../sql-grammar/model';
import { DdlEditError } from '../../types';
import type { DdlEdit, DdlStructure, Identifier, PlannedByteEdit } from '../../types';

import { pickAppendInsertion } from './appendInsertion';
import type { DdlEditCtx } from './applyDdlEdit';
import {
  synthesizeAlterAddColumn,
  synthesizeAlterDropColumn,
  synthesizeAlterRenameColumn,
} from './dialect';
import { foldAsStored, type ExistingNameOrigin } from './dialect.quoting';

type AlterTableStruct = Extract<DdlStructure, { kind: 'alter-table' }>;
type ColumnEdit = Extract<DdlEdit, { op: 'add-column' | 'drop-column' | 'rename-column' }>;

export function planOnAlterTable(
  ctx: DdlEditCtx,
  s: AlterTableStruct,
  edit: ColumnEdit,
): readonly PlannedByteEdit[] {
  if (edit.op === 'add-column' && ctx.engine === 'sqlite'
      && edit.column.nullable === false && edit.column.defaultText === undefined) {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: SQLite ALTER TABLE … ADD COLUMN does not support NOT NULL `
        + `without DEFAULT (v1 limitation; see CONSUMER_HANDOFF.md)`,
    );
  }
  if (edit.op === 'add-column' && ctx.engine === 'sqlite'
      && edit.column.identity !== undefined) {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: SQLite ALTER TABLE … ADD COLUMN does not support `
        + `AUTOINCREMENT (only valid on INTEGER PRIMARY KEY columns at CREATE `
        + `TABLE time). This ALTER TABLE source can't use the recreate path — `
        + `pass the table's CREATE TABLE statement instead with `
        + `'sqliteRecreate: true' for the CREATE-COPY-RENAME-DROP recreate path.`,
    );
  }

  const synth = synthesizeForAlter(ctx, s, edit);
  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source, ctx.statementInfo.range.endOffset,
  );
  return [{
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + synth + trailSep,
    rationale: `${edit.op}:alter-table`,
  }];
}

/** The refusal's one stable fragment. Lives beside the throw so a caller that
 *  has to RECOGNISE this refusal cannot drift from the text that raises it. */
const AMBIGUOUS_MARKER = 'is ambiguous — this statement declares it BOTH delimited and bare';

/** WHERE the spelling an edit uses for an EXISTING column came from, as far as
 *  the SOURCE STATEMENT can tell us. A source clause that wrote the column
 *  DELIMITED created it with exactly those bytes — delimiters suppressed the
 *  fold there — so an emitted statement naming it must not fold them either
 *  (`dialect.quoting.ts`'s `ExistingNameOrigin`). Without this the public edit
 *  API changed a quoted source column's identity: a PostgreSQL
 *  `ALTER TABLE "users" ADD COLUMN "Id" integer` renamed through `oldName:
 *  'Id'` went out as `RENAME COLUMN "id"`, and Oracle as `"ID"` — a column that
 *  does not exist, or a distinct sibling that does.
 *
 *  A CREATE TABLE source answers the SAME question from its column list: the
 *  delimiters in `CREATE TABLE "t" ("Id" integer)` suppress the fold exactly
 *  as an `ADD COLUMN "Id"`'s do, so that statement creates `Id` and an edit
 *  naming it may not fold those bytes either. That shape is what pg_dump, EF
 *  Core and Prisma all emit, so it is the common source, not an exotic one.
 *
 *  Only clauses that leave a column EXISTING are read: `ADD COLUMN <n>`,
 *  `RENAME COLUMN … TO <n>`, and a CREATE TABLE's own column list. A dropped
 *  column is not there to be renamed, and a column this statement never
 *  mentions keeps the default `bare-reference` — that is the contract the
 *  edit's caller is under, and nothing here can improve on it.
 *
 *  An `ALTER COLUMN` / oracle `MODIFY` clause is deliberately NOT read, and
 *  the reason is parity rather than shape: the three engines that have the
 *  clause (`MODIFY` on oracle and mysql, `ALTER COLUMN` on postgres and
 *  mssql) would all have to be read together or the answer would differ by
 *  engine for one identical source shape, and the clause has no reachable
 *  consumer left. The Visual DDL editor's column rows come from
 *  `columnRowsFromStructure`, which renders `add-column` / `drop-column` /
 *  `rename-column` clauses and a CREATE TABLE's columns and SKIPS opaque
 *  ones — so no per-row edit can name a column only a MODIFY mentions — and
 *  the free-text constraint forms, which can, now carry the typed-delimiter
 *  answer instead (`dialect.quoting.ts`'s `decodeTypedColumnRef`). Revisit
 *  this the day a surface names a column off one of those clauses; it is a
 *  recorded gap, not an accident.
 *
 *  A name that the source declares TWICE with DIFFERENT delimiters is
 *  REFUSED, not guessed — see {@link declaredDelimiters}.
 *
 *  Exported for `alterColumnEdit.ts` / `primaryKeyEdit.ts` (and the key/index
 *  planners): each names an existing column under the same contract, and
 *  asking the question in a second place is how the answers would drift. */
export function sourceSpellingOrigin(s: DdlStructure, name: string): ExistingNameOrigin {
  const declared = declaredDelimiters(s, name);
  const delimited = declared.filter(Boolean).length;
  if (delimited > 0 && delimited < declared.length) {
    throw new DdlEditError(
      'invalid-edit',
      `applyDdlEdit: '${name}' ${AMBIGUOUS_MARKER}, which on a folding engine `
        + `are two different columns (PostgreSQL stores the bare one as `
        + `'${foldAsStored(name, 'postgres')}', Oracle as `
        + `'${foldAsStored(name, 'oracle')}'). A name `
        + `alone cannot say which one this edit means, so name the origin `
        + `explicitly — pass 'columnNameOrigin' ('oldNameOrigin' on a rename): `
        + `'delimited-source' for the column the source wrote in delimiters, `
        + `'bare-reference' for the one it wrote bare.`,
    );
  }
  return delimited > 0 ? 'delimited-source' : 'bare-reference';
}

/** Whether `err` is {@link sourceSpellingOrigin}'s ambiguity refusal.
 *
 *  The message names the PAYLOAD FIELD that resolves it, which is the right
 *  answer for an API caller and the wrong one for a person: the Visual DDL
 *  editor's column form renders a failed commit's message inline, so a UI
 *  surface asks this and writes its own sentence instead (`code` alone cannot
 *  tell this refusal from any other malformed edit). Asked here, not by
 *  matching text at the call site, for the reason `DdlEditError`'s header
 *  gives — consumers must not string-match a message they do not own. */
export function isAmbiguousColumnNameError(err: unknown): err is DdlEditError {
  return err instanceof DdlEditError
    && err.code === 'invalid-edit'
    && err.message.includes(AMBIGUOUS_MARKER);
}

/** The column name {@link sourceSpellingOrigin}'s refusal is about, so a UI
 *  surface can name it in a sentence of its own.
 *
 *  Read back out of the message rather than off a field, because the message
 *  is where the throw puts it and this lives in the same module as the throw —
 *  the reader and the template are edited together or not at all. Widening
 *  `DdlEditError` with a per-refusal payload would buy the same thing at the
 *  cost of a shared type every other refusal carries dead. `null` for any
 *  other error, and for a message the delimiters do not bracket. */
export function ambiguousColumnNameOf(err: unknown): string | null {
  if (!isAmbiguousColumnNameError(err)) return null;
  const prefix = "applyDdlEdit: '";
  const end = err.message.indexOf(`' ${AMBIGUOUS_MARKER}`);
  if (!err.message.startsWith(prefix) || end < prefix.length) return null;
  const name = err.message.slice(prefix.length, end);
  return name.length > 0 ? name : null;
}

/** One entry per OCCURRENCE this statement declares of `name`: `true` when
 *  that occurrence carried delimiters, `false` when it did not.
 *
 *  The list is the whole point. `.some()` over it answered `true` the moment
 *  ANY occurrence was delimited, so a `CREATE TABLE t (Id integer, "Id"
 *  integer)` — two columns, `id` and `Id` on PostgreSQL — handed the bare
 *  column's edit the delimited one's spelling and quietly retargeted it. Two
 *  occurrences that DISAGREE mean the request is under-specified, and the
 *  caller has to say which; two that agree are not ambiguous at all, and an
 *  empty list is the `'bare-reference'` default every caller holding only a
 *  name is under.
 *
 *  An occurrence whose quoting the extractor did not record reads as NOT
 *  delimited, which is the posture that stood before: it is not evidence FOR
 *  delimiters, and treating it as its own third answer would refuse edits on
 *  every hand-built structure.
 *
 *  MATCHING BY EXACT BYTES IS LOAD-BEARING, and the public docs now rest on
 *  it. A user typing the STORED spelling into a key list — `id` where the
 *  source declared `Id` bare and `"Id"` delimited — must match NEITHER
 *  declaration, so the empty list falls to `'bare-reference'`, the entry is
 *  emitted undelimited, and the server folds it onto the column it declared
 *  bare. Typing the stored spelling DELIMITED (`'"id"'`) reaches the same
 *  column by the other route — `decodeTypedColumnRef` answers
 *  `'catalog-spelling'` for those bytes, which name it exactly — so the two
 *  routes agree; what no spelling can do is name the bare column with the
 *  DECLARED bytes.
 *  A fold-aware matcher would still answer correctly IF it compared STORED
 *  identities (bare `Id` → `id`; `"Id"` → `Id`), so typed `id` matches only
 *  the first; one that merely lower-cased bytes would match both, re-raise the
 *  ambiguity, and take that route away. */
function declaredDelimiters(s: DdlStructure, name: string): readonly boolean[] {
  if (s.kind === 'create-table') {
    return s.columns
      .filter((col) => col.name === name)
      .map((col) => isDelimited(col.nameQuoting));
  }
  if (s.kind !== 'alter-table') return [];
  const out: boolean[] = [];
  for (const clause of s.clauses) {
    if (clause.kind === 'add-column' && clause.column.name === name) {
      out.push(isDelimited(clause.nameQuoting));
    }
    if (clause.kind === 'rename-column' && clause.newName === name) {
      out.push(isDelimited(clause.newNameQuoting));
    }
    if (clause.kind === 'opaque') out.push(...declaredInGroup(clause.addedColumns, name));
  }
  return out;
}

/** The occurrences of `name` a parenthesised `ADD ( … )` group declares, with
 *  the delimiters each carried. The group stays an opaque clause — v1 does not
 *  edit it — but the columns it creates exist just as much as a single-column
 *  `ADD`'s do, and on Oracle the paren form is the ONLY way to add more than
 *  one. */
function declaredInGroup(
  addedColumns: readonly Identifier[] | undefined,
  name: string,
): readonly boolean[] {
  return (addedColumns ?? [])
    .filter((col) => col.name === name)
    .map((col) => isDelimited(col.quoting));
}

function isDelimited(quoting: IdentifierQuoting | undefined): boolean {
  return quoting !== undefined && quoting !== 'unquoted';
}

function synthesizeForAlter(
  ctx: DdlEditCtx,
  s: AlterTableStruct,
  edit: ColumnEdit,
): string {
  if (edit.op === 'add-column') {
    return synthesizeAlterAddColumn(ctx.engine, s.table, edit.column);
  }
  if (edit.op === 'drop-column') {
    return synthesizeAlterDropColumn(
      ctx.engine, s.table, edit.columnName, s.table.quoted,
      edit.columnNameOrigin ?? sourceSpellingOrigin(s, edit.columnName),
    );
  }
  return synthesizeAlterRenameColumn(
    ctx.engine, s.table, edit.oldName, edit.newName, s.table.quoted,
    undefined,
    edit.oldNameOrigin ?? sourceSpellingOrigin(s, edit.oldName),
  );
}

